// @flow
import * as React from 'react';
import connectWS from 'speed-js-react/src/websocket/connectWS';
import WebSocketClient from 'speed-js-core/src/websocket/WebSocketClient';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'speed-js-react';
import { getIsLandingTab, getIsLandingTabInSameBrowser } from 'esm-web-pro/redux/landingTab';
import {
    getCallingState, setCallingsState, updateOperatorPresence, wsDispatchAction,
} from 'esm-web-pro/redux/callings';
import type { CallState } from 'esm-web-pro/redux/callings/reducer';
import { workingTabPBXActionTypes } from 'esm-web-pro/redux/callings/constants';

type Props = {
    ws: WebSocketClient,
}

export const getPBXWSGroup = (user: Object) => `pbx.state.${user.id}`;
export const PBX_WS_COMMAND = 'pbx-state';
const USER_CHANGES_WS_GROUP = 'changes.authorisation.user';

type WSData = {
    dispatch_action: {
        payload: {
            type: $Keys<workingTabPBXActionTypes>,
            payload: any
        },
        source_connection: UUID
    }
} | {
    set_state: {
        state: $Shape<CallState>,
        connection: UUID,
    }
}

const PBXWSController = (props: Props) => {
    const { ws } = props;
    const [subscribed, setSubscribed] = React.useState(false);

    const user = useSelector(getUser);
    const dispatch = useDispatch();
    const isLandingTab = useSelector(getIsLandingTab);
    const isLandingTabInSameBrowser = useSelector(getIsLandingTabInSameBrowser);

    const callingState = useSelector(getCallingState);

    const wsGroup = user ? getPBXWSGroup(user) : null;

    const handleWSData = React.useCallback((data: WSData) => {
        // There are two types of actions which can be received by the backend:
        // "dispatch_action" and "set_state"

        // The first specifies an action which should be dispatched to the redux store using the 'wsDispatchAction'
        // (not dispatched directly in order to indicate that the action has been initiated by another tab)
        if (data.dispatch_action
                && ws.connectionId !== data.dispatch_action.source_connection) {
            dispatch(
                wsDispatchAction(data.dispatch_action.payload),
            );
            // The "set_state" action means that the whole PBX state in redux should be updated ->
            // this action is received only after a 'get_current_state' action has been send through the websocket
            // after the working tab has become visible
        } else if (data.set_state && data.set_state.connection === ws.connectionId) {
            dispatch(
                setCallingsState(data.set_state.state),
            );
        }
    }, [dispatch, ws.connectionId]);

    const handleUserChanges = React.useCallback((data) => {
        if (data.event === 'update' && data.meta.values.pbx_status) {
            dispatch(updateOperatorPresence(data.meta.values.uuid, data.meta.values.pbx_status));
        }
    }, [dispatch]);

    React.useEffect(() => {
        // Effect which sends a websocket message with the current state to the backend on every state update
        // This is done only by the landing tab (and should be run only after
        // the landing tab has subscribed to the PBX group)
        if (subscribed && isLandingTab) {
            ws.send(PBX_WS_COMMAND, {
                action: 'set_state',
                payload: callingState,
            });
        }
    }, [ws, wsGroup, isLandingTab, callingState, subscribed]);

    React.useEffect(() => {
        // Effect which handles the subscription of the landing tab
        if (wsGroup && isLandingTab !== null && isLandingTab) {
            ws.subscribe({
                [wsGroup]: handleWSData,
                [USER_CHANGES_WS_GROUP]: handleUserChanges,
            });

            ws.onSubscribe(wsGroup, () => {
                setSubscribed(true);
            });
        }
        return () => {
            if (wsGroup && isLandingTab !== null && isLandingTab) {
                ws.unSubscribe([wsGroup, USER_CHANGES_WS_GROUP]);
            }
        };
    }, [ws, wsGroup, isLandingTab, handleWSData, isLandingTabInSameBrowser, handleUserChanges]);

    const workingTabSubscribeHandler = React.useCallback(() => {
        // Working tabs should subscribe to state changes only if they're visible
        if (document.visibilityState === 'visible') {
            ws.subscribe({
                [wsGroup]: handleWSData,
            });

            ws.onSubscribe(wsGroup, () => {
                ws.send(PBX_WS_COMMAND, {
                    action: 'get_current_state',
                });
            });
        } else {
            ws.unSubscribe([wsGroup]);
        }
    }, [ws, handleWSData, wsGroup]);

    React.useEffect(() => {
        // Effect which handles the subscription of the working tabs
        if (wsGroup && isLandingTab !== null && (isLandingTabInSameBrowser && !isLandingTab)) {
            document.addEventListener('visibilitychange', workingTabSubscribeHandler);

            // Initial subscription if the tab is already visible
            if (document.visibilityState === 'visible') {
                ws.subscribe({
                    [wsGroup]: handleWSData,
                });

                ws.onSubscribe(wsGroup, () => {
                    ws.send(PBX_WS_COMMAND, {
                        action: 'get_current_state',
                    });
                });
            }
        }
        return () => {
            if (wsGroup && isLandingTab !== null && isLandingTabInSameBrowser && !isLandingTab) {
                document.removeEventListener('visibilitychange', workingTabSubscribeHandler);
            }
        };
    }, [workingTabSubscribeHandler, ws, wsGroup, isLandingTab, handleWSData, isLandingTabInSameBrowser]);

    return null;
};

export default connectWS(PBXWSController);
